import React, { useMemo, useState } from "react";

import {
  DeleteOutline as ClearIcon,
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
  CheckCircleOutline as SuccessIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CallHistoryEntry,
  MAX_HISTORY_ITEMS,
} from "../../../helpers/grpc-call-history";

interface CallHistoryPanelProps {
  entries: CallHistoryEntry[];
  onReplay: (entry: CallHistoryEntry) => void;
  onClear: () => void;
  onTogglePin: (id: string) => void;
  /** Whether an entry can be replayed into the form (log-only otherwise). */
  canReplay: (entry: CallHistoryEntry) => boolean;
}

type SourceFilter = "dev-tools" | "all";

const entrySx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  mb: 0.5,
  py: 0.75,
  px: 1,
  alignItems: "flex-start",
  flexDirection: "column",
  gap: 0.25,
  // Reserve room on the right so entry content never underlaps the pin button.
  pr: 5,
} as const;

/** Compact relative-time label, e.g. "12s ago", "3m ago", "2h ago". */
const relativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const EntryMeta: React.FC<{ entry: CallHistoryEntry }> = ({ entry }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      width: "100%",
    }}
  >
    {entry.success ? (
      <SuccessIcon color="success" sx={{ fontSize: 16 }} />
    ) : (
      <ErrorIcon color="error" sx={{ fontSize: 16 }} />
    )}
    <Typography
      variant="caption"
      sx={{
        fontWeight: 400,
        fontFamily: "monospace",
        flexGrow: 1,
        minWidth: 0,
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {entry.methodName}
    </Typography>
    {entry.source === "dev-tools" && (
      <Chip
        label="dev-tools"
        size="small"
        variant="outlined"
        color="primary"
        sx={{ fontSize: "0.6rem", height: 16, "& .MuiChip-label": { px: 0.5 } }}
      />
    )}
    {entry.region && (
      <Chip
        label={entry.region.toUpperCase()}
        size="small"
        variant="outlined"
        sx={{ fontSize: "0.6rem", height: 16, "& .MuiChip-label": { px: 0.5 } }}
      />
    )}
  </Box>
);

const EntrySubline: React.FC<{
  entry: CallHistoryEntry;
  replayable: boolean;
}> = ({ entry, replayable }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1,
      flexWrap: "wrap",
      pl: "22px",
      width: "100%",
      minWidth: 0,
      color: "text.secondary",
    }}
  >
    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
      {relativeTime(entry.timestamp)}
    </Typography>
    {entry.durationMs !== undefined && (
      <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
        {entry.durationMs}ms
      </Typography>
    )}
    {entry.referenceId && (
      <Tooltip title={`role: ${entry.referenceId}`}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.7rem",
            flexBasis: "100%",
            minWidth: 0,
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          role: {entry.referenceId}
        </Typography>
      </Tooltip>
    )}
    {!replayable && (
      <Typography
        variant="caption"
        sx={{ fontStyle: "italic", fontSize: "0.7rem" }}
      >
        log only
      </Typography>
    )}
  </Box>
);

const EntryItem: React.FC<{
  entry: CallHistoryEntry;
  replayable: boolean;
  onReplay: (entry: CallHistoryEntry) => void;
  onTogglePin: (id: string) => void;
}> = ({ entry, replayable, onReplay, onTogglePin }) => {
  // Rendered as a sibling of the button (not nested inside it) so the pin
  // control is a separate click target and keeps valid list/button semantics.
  const pinAction = (
    <IconButton
      edge="end"
      size="small"
      aria-label={entry.pinned ? "Unpin call" : "Pin call"}
      onClick={() => onTogglePin(entry.id)}
    >
      {entry.pinned ? (
        <StarIcon fontSize="small" color="warning" />
      ) : (
        <StarBorderIcon fontSize="small" />
      )}
    </IconButton>
  );

  return replayable ? (
    <ListItem disablePadding secondaryAction={pinAction}>
      <ListItemButton onClick={() => onReplay(entry)} sx={entrySx}>
        <EntryMeta entry={entry} />
        <EntrySubline entry={entry} replayable />
      </ListItemButton>
    </ListItem>
  ) : (
    <ListItem sx={entrySx} secondaryAction={pinAction}>
      <EntryMeta entry={entry} />
      <EntrySubline entry={entry} replayable={false} />
    </ListItem>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{
      display: "block",
      mt: 0.25,
      mb: 0.25,
      letterSpacing: 0.5,
      fontSize: "0.65rem",
    }}
  >
    {children}
  </Typography>
);

export const CallHistoryPanel: React.FC<CallHistoryPanelProps> = ({
  entries,
  onReplay,
  onClear,
  onTogglePin,
  canReplay,
}) => {
  const [filter, setFilter] = useState<SourceFilter>("all");

  const visible = useMemo(
    () =>
      filter === "dev-tools"
        ? entries.filter((e) => e.source === "dev-tools")
        : entries,
    [entries, filter],
  );

  const pinned = useMemo(() => visible.filter((e) => e.pinned), [visible]);
  const recent = useMemo(() => visible.filter((e) => !e.pinned), [visible]);

  const renderList = (list: CallHistoryEntry[]): React.ReactNode => (
    <List dense disablePadding>
      {list.map((entry) => (
        <EntryItem
          key={entry.id}
          entry={entry}
          replayable={canReplay(entry)}
          onReplay={onReplay}
          onTogglePin={onTogglePin}
        />
      ))}
    </List>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.75,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_, value) => value && setFilter(value)}
            aria-label="history source filter"
          >
            <ToggleButton value="dev-tools" sx={{ textTransform: "none" }}>
              Dev-tools
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: "none" }}>
              All app calls
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip
            title={`Recorded while “Log gRPC to console” is on. Last ${MAX_HISTORY_ITEMS} calls; pinned kept beyond that and survive Clear.`}
          >
            <InfoIcon
              fontSize="small"
              color="action"
              aria-label="About call history"
            />
          </Tooltip>
        </Box>
        <Button
          size="small"
          startIcon={<ClearIcon fontSize="small" />}
          onClick={onClear}
          sx={{ textTransform: "none" }}
        >
          Clear history
        </Button>
      </Box>

      {visible.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {filter === "dev-tools"
              ? "No dev-tools calls yet — execute a method (with logging on) to see it here."
              : "No calls recorded yet — enable “Log gRPC to console” and use the app."}
          </Typography>
        </Box>
      ) : (
        <>
          {pinned.length > 0 && (
            <>
              <SectionLabel>Pinned ({pinned.length})</SectionLabel>
              {renderList(pinned)}
            </>
          )}
          {recent.length > 0 && (
            <>
              {pinned.length > 0 && <SectionLabel>Recent</SectionLabel>}
              {renderList(recent)}
            </>
          )}
        </>
      )}
    </Box>
  );
};
